import React, { useRef, useState, useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from "recharts";
import Select from "react-select";
import html2canvas from "html2canvas";

const PatientVisitFrequencyChart = ({ data }) => {
  const chartRef = useRef(null);
  const [selectedDiagnoses, setSelectedDiagnoses] = useState([]);

  // Define visit frequency ranges
  const frequencyRanges = [
    { label: "0-10", value: "0-10", min: 0, max: 10 },
    { label: "11-20", value: "11-20", min: 11, max: 20 },
    { label: "21-30", value: "21-30", min: 21, max: 30 },
    { label: "31-40", value: "31-40", min: 31, max: 40 },
    { label: "41-50", value: "41-50", min: 41, max: 50 },
    { label: "50+", value: "50+", min: 51, max: Infinity },
  ];

  // Get unique diagnoses from data
  const uniqueDiagnoses = useMemo(() => {
    const diagnoses = new Set();
    data.forEach((item) => {
      const diagnosis =
        item.patient_record?.diagnoses?.diagnosis_name || "Unknown";
      if (diagnosis !== "Unknown") {
        diagnoses.add(diagnosis);
      }
    });
    return Array.from(diagnoses).map((diagnosis) => ({
      value: diagnosis,
      label: diagnosis,
    }));
  }, [data]);

  // React-select options for diagnoses
  const diagnosisOptions = [
    { value: "all", label: "All Diagnoses" },
    ...uniqueDiagnoses,
  ];

  // Custom styles for react-select
  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "42px",
      border: state.isFocused ? "2px solid #2684FF" : "1px solid #ccc",
      borderRadius: "4px",
      boxShadow: state.isFocused ? "0 0 0 1px #2684FF" : "none",
      "&:hover": {
        borderColor: state.isFocused ? "#2684FF" : "#999",
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#2684FF"
        : state.isFocused
        ? "#deebff"
        : "white",
      color: state.isSelected ? "white" : "black",
      padding: "8px 12px",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#2684FF",
      color: "white",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "white",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "white",
      "&:hover": {
        backgroundColor: "#0056b3",
        color: "white",
      },
    }),
  };

  // Aggregate data to count visits per patient and collect diagnosis info
  const visitFrequency = useMemo(() => {
    return data.reduce((acc, item) => {
      const patientName = item.patient_name;
      if (!acc[patientName]) {
        acc[patientName] = {
          visits: 0,
          diagnosis:
            item.patient_record?.diagnoses?.diagnosis_name || "Unknown",
        };
      }
      acc[patientName].visits++;
      return acc;
    }, {});
  }, [data]);

  // Transform data for the chart with frequency intervals
  const chartData = useMemo(() => {
    // Group patients by visit frequency range
    const rangeData = frequencyRanges.map((range) => {
      const patientsInRange = Object.entries(visitFrequency).filter(
        ([patientName, data]) => {
          if (range.max === Infinity) {
            return data.visits >= range.min;
          }
          return data.visits >= range.min && data.visits <= range.max;
        }
      );

      // Count total patients in this range
      const totalPatients = patientsInRange.length;

      // Group by diagnosis
      const diagnosisCounts = {};
      patientsInRange.forEach(([patientName, data]) => {
        const diagnosis = data.diagnosis;
        if (diagnosis !== "Unknown") {
          diagnosisCounts[diagnosis] = (diagnosisCounts[diagnosis] || 0) + 1;
        }
      });

      return {
        range: range.label,
        totalPatients,
        ...diagnosisCounts,
      };
    });

    return rangeData;
  }, [visitFrequency, frequencyRanges]);

  // Get available diagnoses for the chart (those that have data)
  const availableDiagnoses = useMemo(() => {
    const diagnoses = new Set();
    chartData.forEach((item) => {
      Object.keys(item).forEach((key) => {
        if (key !== "range" && key !== "totalPatients" && item[key] > 0) {
          diagnoses.add(key);
        }
      });
    });
    return Array.from(diagnoses);
  }, [chartData]);

  // Get diagnoses to display in chart (selected or all if none selected)
  const diagnosesToDisplay = useMemo(() => {
    if (
      selectedDiagnoses.length === 0 ||
      selectedDiagnoses.some((d) => d.value === "all")
    ) {
      return availableDiagnoses;
    }
    return selectedDiagnoses
      .map((d) => d.value)
      .filter((diagnosis) => availableDiagnoses.includes(diagnosis));
  }, [selectedDiagnoses, availableDiagnoses]);

  // Filter chart data based on selected diagnoses
  const filteredChartData = useMemo(() => {
    if (
      selectedDiagnoses.length === 0 ||
      selectedDiagnoses.some((d) => d.value === "all")
    ) {
      return chartData;
    }

    const selectedDiagnosisValues = selectedDiagnoses.map((d) => d.value);

    return chartData.map((item) => {
      const filteredItem = {
        range: item.range,
        totalPatients: item.totalPatients,
      };

      // Only include selected diagnoses
      selectedDiagnosisValues.forEach((diagnosis) => {
        if (item[diagnosis] !== undefined) {
          filteredItem[diagnosis] = item[diagnosis];
        }
      });

      return filteredItem;
    });
  }, [chartData, selectedDiagnoses]);

  // Get statistics for the current filter
  const getStatistics = () => {
    // Calculate statistics based on selected diagnoses
    let totalPatients = 0;
    let totalVisits = 0;

    if (
      selectedDiagnoses.length === 0 ||
      selectedDiagnoses.some((d) => d.value === "all")
    ) {
      // Show all patients if no selection or "All Diagnoses" selected
      totalPatients = chartData.reduce(
        (sum, item) => sum + item.totalPatients,
        0
      );
      totalVisits = Object.values(visitFrequency).reduce(
        (sum, data) => sum + data.visits,
        0
      );
    } else {
      // Calculate totals only for selected diagnoses
      const selectedDiagnosisValues = selectedDiagnoses.map((d) => d.value);

      // Calculate patients and visits for selected diagnoses only
      Object.values(visitFrequency).forEach((data) => {
        if (selectedDiagnosisValues.includes(data.diagnosis)) {
          totalPatients++;
          totalVisits += data.visits;
        }
      });
    }

    const avgVisits =
      totalPatients > 0 ? (totalVisits / totalPatients).toFixed(1) : 0;

    // Calculate diagnosis-wise statistics
    const diagnosisStats = Object.values(visitFrequency).reduce((acc, data) => {
      const diagnosis = data.diagnosis;
      if (diagnosis !== "Unknown") {
        if (!acc[diagnosis]) {
          acc[diagnosis] = { patients: 0, visits: 0 };
        }
        acc[diagnosis].patients++;
        acc[diagnosis].visits += data.visits;
      }
      return acc;
    }, {});

    // Convert to array and sort by number of patients
    const diagnosisBreakdown = Object.entries(diagnosisStats)
      .map(([diagnosis, stats]) => ({
        diagnosis,
        patients: stats.patients,
        visits: stats.visits,
        avgVisits:
          stats.patients > 0 ? (stats.visits / stats.patients).toFixed(1) : 0,
      }))
      .sort((a, b) => b.patients - a.patients);

    return { totalPatients, totalVisits, avgVisits, diagnosisBreakdown };
  };

  const handleDownload = async () => {
    if (chartRef.current) {
      try {
        // Find the visible chart container within chartRef
        const visibleChart =
          chartRef.current.querySelector(".recharts-wrapper");

        if (!visibleChart) {
          alert("Chart not found. Please try again.");
          return;
        }

        // Get statistics
        const stats = getStatistics();

        // Create a temporary container for the chart
        const tempContainer = document.createElement("div");
        tempContainer.style.cssText = `
          position: absolute;
          left: -9999px;
          top: 0;
          width: 1000px;
          height: 800px;
          backgroundColor: #ffffff;
          padding: 30px;
          border: 1px solid #ddd;
          borderRadius: 8px;
          boxShadow: 0 2px 4px rgba(0,0,0,0.1);
          zIndex: -1;
        `;

        // Add title
        const title = document.createElement("h3");
        title.textContent = `Patient Visit Frequency Chart${
          selectedDiagnoses.length > 0 &&
          !selectedDiagnoses.some((d) => d.value === "all")
            ? ` - ${selectedDiagnoses.map((d) => d.label).join(", ")}`
            : ""
        }`;
        title.style.cssText = `
          textAlign: center;
          marginBottom: 15px;
          fontSize: 20px;
          fontWeight: bold;
          color: #333;
        `;
        tempContainer.appendChild(title);

        // Add statistics
        const statsDiv = document.createElement("div");
        statsDiv.style.cssText = `
          display: flex;
          justifyContent: center;
          gap: 30px;
          marginBottom: 20px;
          padding: 15px;
          backgroundColor: #f8f9fa;
          borderRadius: 8px;
          border: 1px solid #e9ecef;
        `;

        const statItems = [
          { label: "Total Patients", value: stats.totalPatients },
          { label: "Total Visits", value: stats.totalVisits },
          { label: "Average Visits", value: stats.avgVisits },
        ];

        statItems.forEach((stat) => {
          const statItem = document.createElement("div");
          statItem.style.cssText = `
            textAlign: center;
            padding: 0 15px;
          `;

          const label = document.createElement("div");
          label.textContent = stat.label;
          label.style.cssText = `
            fontSize: 14px;
            color: #666;
            marginBottom: 5px;
            fontWeight: 500;
          `;

          const value = document.createElement("div");
          value.textContent = stat.value;
          value.style.cssText = `
            fontSize: 18px;
            fontWeight: bold;
            color: #333;
          `;

          statItem.appendChild(label);
          statItem.appendChild(value);
          statsDiv.appendChild(statItem);
        });

        tempContainer.appendChild(statsDiv);

        // Add diagnosis breakdown
        if (stats.diagnosisBreakdown.length > 0) {
          const diagnosisBreakdownDiv = document.createElement("div");
          diagnosisBreakdownDiv.style.cssText = `
            marginTop: 20px;
            padding: 15px;
            backgroundColor: #f8f9fa;
            borderRadius: 8px;
            border: 1px solid #e9ecef;
          `;

          const diagnosisTitle = document.createElement("h4");
          diagnosisTitle.textContent = "Diagnosis Breakdown";
          diagnosisTitle.style.cssText = `
            marginBottom: 15px;
            fontSize: 16px;
            fontWeight: bold;
            color: #333;
          `;
          diagnosisBreakdownDiv.appendChild(diagnosisTitle);

          const diagnosisTable = document.createElement("table");
          diagnosisTable.style.cssText = `
            width: 100%;
            borderCollapse: collapse;
            fontSize: 12px;
          `;

          // Create table header
          const thead = document.createElement("thead");
          const headerRow = document.createElement("tr");
          const headers = [
            "Diagnosis",
            "Patients",
            "Total Visits",
            "Avg Visits",
          ];

          headers.forEach((headerText) => {
            const th = document.createElement("th");
            th.textContent = headerText;
            th.style.cssText = `
              backgroundColor: #f2f2f2;
              padding: 8px;
              borderBottom: 1px solid #ddd;
              textAlign: left;
              fontSize: 12px;
            `;
            headerRow.appendChild(th);
          });
          thead.appendChild(headerRow);
          diagnosisTable.appendChild(thead);

          // Create table body
          const tbody = document.createElement("tbody");
          stats.diagnosisBreakdown.forEach((item) => {
            const row = document.createElement("tr");

            const diagnosisCell = document.createElement("td");
            diagnosisCell.textContent = item.diagnosis;
            diagnosisCell.style.cssText = `
              padding: 8px;
              borderBottom: 1px solid #ddd;
              fontSize: 12px;
            `;

            const patientsCell = document.createElement("td");
            patientsCell.textContent = item.patients;
            patientsCell.style.cssText = `
              padding: 8px;
              borderBottom: 1px solid #ddd;
              textAlign: center;
              fontSize: 12px;
            `;

            const visitsCell = document.createElement("td");
            visitsCell.textContent = item.visits;
            visitsCell.style.cssText = `
              padding: 8px;
              borderBottom: 1px solid #ddd;
              textAlign: center;
              fontSize: 12px;
            `;

            const avgVisitsCell = document.createElement("td");
            avgVisitsCell.textContent = item.avgVisits;
            avgVisitsCell.style.cssText = `
              padding: 8px;
              borderBottom: 1px solid #ddd;
              textAlign: center;
              fontSize: 12px;
            `;

            row.appendChild(diagnosisCell);
            row.appendChild(patientsCell);
            row.appendChild(visitsCell);
            row.appendChild(avgVisitsCell);
            tbody.appendChild(row);
          });
          diagnosisTable.appendChild(tbody);
          diagnosisBreakdownDiv.appendChild(diagnosisTable);
          tempContainer.appendChild(diagnosisBreakdownDiv);
        }

        // Clone the chart
        const chartClone = visibleChart.cloneNode(true);
        chartClone.style.width = "100%";
        chartClone.style.height = "500px";
        tempContainer.appendChild(chartClone);

        // Add to DOM temporarily
        document.body.appendChild(tempContainer);

        // Wait for rendering
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Capture the temporary container
        const canvas = await html2canvas(tempContainer, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: "#ffffff",
          logging: false,
          imageTimeout: 15000,
          removeContainer: false,
          width: 1000,
          height: 1000,
        });

        // Remove temporary container
        document.body.removeChild(tempContainer);

        // Create download
        const imgData = canvas.toDataURL("image/png", 1.0);
        const link = document.createElement("a");
        link.href = imgData;
        const diagnosisLabel =
          selectedDiagnoses.length > 0 &&
          !selectedDiagnoses.some((d) => d.value === "all")
            ? selectedDiagnoses.map((d) => d.value).join("_")
            : "all";
        link.download = `patient_visit_frequency_${diagnosisLabel}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("Download failed:", error);
        alert("Failed to download chart. Please try again.");
      }
    }
  };

  const statistics = getStatistics();

  // Generate colors for different diagnoses
  const getBarColor = (index) => {
    const colors = [
      "#8884d8",
      "#82ca9d",
      "#ffc658",
      "#ff7300",
      "#ff0000",
      "#00ff00",
      "#0000ff",
      "#ffff00",
    ];
    return colors[index % colors.length];
  };

  return (
    <div>
      <h2>Patient Visit Frequency</h2>

      {/* Filter Controls */}
      <div style={{ marginBottom: "20px" }}>
        <label style={{ marginRight: "10px", fontWeight: "bold" }}>
          Filter by Diagnosis:
        </label>
        <Select
          options={diagnosisOptions}
          value={selectedDiagnoses}
          onChange={(selectedOptions) =>
            setSelectedDiagnoses(selectedOptions || [])
          }
          styles={selectStyles}
          placeholder="Select diagnoses"
          isMulti
          isSearchable={true}
          closeMenuOnSelect={false}
        />
      </div>

      {/* Statistics */}
      <div
        style={{
          display: "flex",
          gap: "20px",
          marginBottom: "20px",
          padding: "15px",
          backgroundColor: "#f8f9fa",
          borderRadius: "8px",
        }}
      >
        <div>
          <strong>Total Patients:</strong> {statistics.totalPatients}
        </div>
        <div>
          <strong>Total Visits:</strong> {statistics.totalVisits}
        </div>
        <div>
          <strong>Average Visits:</strong> {statistics.avgVisits}
        </div>
      </div>

      <div ref={chartRef}>
        {filteredChartData.length > 0 ? (
          <>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={filteredChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="range"
                  label={{
                    value: "Number of Visits",
                    position: "insideBottom",
                    offset: -10,
                  }}
                />
                <YAxis
                  label={{
                    value: "Number of Patients",
                    angle: -90,
                    position: "insideLeft",
                    offset: 10,
                  }}
                />
                <Tooltip
                  formatter={(value, name) => [
                    value,
                    name === "totalPatients" ? "Total Patients" : name,
                  ]}
                  labelFormatter={(label) => `Visit Range: ${label}`}
                />
                <Legend />

                {/* Render diagnosis-specific bars */}
                {diagnosesToDisplay.map((diagnosis, index) => (
                  <Bar
                    key={diagnosis}
                    dataKey={diagnosis}
                    fill={getBarColor(index)}
                    name={diagnosis}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>

            {/* Frequency Range Details */}
            <div style={{ marginTop: "20px" }}>
              <h3>Visit Frequency Distribution:</h3>
              <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={tableHeaderStyle}>Visit Range</th>
                      <th style={tableHeaderStyle}>Total Patients</th>
                      {diagnosesToDisplay.map((diagnosis) => (
                        <th key={diagnosis} style={tableHeaderStyle}>
                          {diagnosis}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredChartData.map((item) => (
                      <tr key={item.range}>
                        <td style={tableCellStyle}>{item.range}</td>
                        <td style={tableCellStyle}>{item.totalPatients}</td>
                        {diagnosesToDisplay.map((diagnosis) => (
                          <td key={diagnosis} style={tableCellStyle}>
                            {item[diagnosis] || 0}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Diagnosis Breakdown */}
            {statistics.diagnosisBreakdown.length > 0 && (
              <div style={{ marginTop: "20px" }}>
                <h3>Diagnosis Breakdown:</h3>
                <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th style={tableHeaderStyle}>Diagnosis</th>
                        <th style={tableHeaderStyle}>Patients</th>
                        <th style={tableHeaderStyle}>Total Visits</th>
                        <th style={tableHeaderStyle}>Average Visits</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statistics.diagnosisBreakdown.map(
                        ({ diagnosis, patients, visits, avgVisits }) => (
                          <tr key={diagnosis}>
                            <td style={tableCellStyle}>{diagnosis}</td>
                            <td style={tableCellStyle}>{patients}</td>
                            <td style={tableCellStyle}>{visits}</td>
                            <td style={tableCellStyle}>{avgVisits}</td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              backgroundColor: "#f8f9fa",
              borderRadius: "8px",
            }}
          >
            <p>No data found for the selected diagnoses.</p>
          </div>
        )}
      </div>

      <div style={{ textAlign: "center", marginTop: "20px" }}>
        <button
          onClick={handleDownload}
          disabled={filteredChartData.length === 0}
          style={{
            padding: "10px 20px",
            backgroundColor:
              filteredChartData.length === 0 ? "#ccc" : "#4CAF50",
            color: "white",
            border: "none",
            borderRadius: "5px",
            cursor: filteredChartData.length === 0 ? "not-allowed" : "pointer",
            fontSize: "14px",
          }}
        >
          Download High-Resolution Chart
        </button>
      </div>
    </div>
  );
};

const tableHeaderStyle = {
  backgroundColor: "#f2f2f2",
  padding: "10px",
  borderBottom: "1px solid #ddd",
  textAlign: "left",
  position: "sticky",
  top: 0,
  zIndex: 1,
};

const tableCellStyle = {
  padding: "10px",
  borderBottom: "1px solid #ddd",
};

export default PatientVisitFrequencyChart;
